import React, { useState, useEffect, useCallback } from 'react';
import { rewardService, statsService } from '../api/services';
import RewardItemModal from '../components/RewardItemModal';
import RewardRedeemModal from '../components/RewardRedeemModal';
import RewardReviewList from '../components/RewardReviewList';
import { formatDate } from '../utils/helpers';
import '../components/Reward.css';

const STATUS_LABEL = { PENDING: '待審核', APPROVED: '已同意', REJECTED: '已拒絕', CANCELLED: '已取消' };

// 點數兌換商城：瀏覽商品、選數量提出兌換；具上架權限者可上架/編輯/下架；具審核權限者可審核兌換。
const RewardShopPage = () => {
  const [tab, setTab] = useState('shop');
  const [items, setItems] = useState([]);
  const [flags, setFlags] = useState({ canCreate: false, canReview: false });
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [redeemItem, setRedeemItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [myRedemptions, setMyRedemptions] = useState([]);
  // 待審核兌換筆數（供「兌換審核」分頁計數提示，僅具審核權限者載入）
  const [reviewCount, setReviewCount] = useState(0);

  // 載入剩餘點數（個人帳本淨額）
  const loadBalance = useCallback(() => statsService.getMe()
    .then((res) => setRemaining(res.data?.pointsRemaining ?? 0))
    .catch(() => {}), []);

  const loadItems = useCallback(() => {
    setLoading(true);
    return rewardService.list()
      .then((res) => {
        setItems(res.data?.items || []);
        setFlags({ canCreate: !!res.data?.canCreate, canReview: !!res.data?.canReview });
      })
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  const loadMine = useCallback(() => rewardService.myRedemptions()
    .then((res) => setMyRedemptions(res.data?.items || []))
    .catch((err) => setError(err.response?.data?.error || err.message)), []);

  // 載入待審核筆數（PENDING）供分頁計數
  const loadReviewCount = useCallback(() => rewardService.reviewQueue('PENDING')
    .then((res) => setReviewCount(res.data?.items?.length || 0))
    .catch(() => {}), []);

  // 進站即載入商品、餘額與我的兌換（讓各分頁計數一開始就正確）
  useEffect(() => { loadItems(); loadBalance(); loadMine(); }, [loadItems, loadBalance, loadMine]);
  // 取得審核權限後才載入待審計數
  useEffect(() => { if (flags.canReview) loadReviewCount(); }, [flags.canReview, loadReviewCount]);
  // 切換到「我的兌換」時刷新一次，確保資料最新
  useEffect(() => { if (tab === 'mine') loadMine(); }, [tab, loadMine]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const handleRedeemed = () => {
    flash('已提出兌換申請，待審核');
    loadItems();
    loadBalance();
    loadMine();
    if (flags.canReview) loadReviewCount();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('確定下架此商品？')) return;
    try { await rewardService.remove(id); loadItems(); } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('確定取消此兌換申請？')) return;
    try { await rewardService.cancel(id); loadMine(); loadBalance(); } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  return (
    <div className="container" style={{ paddingTop: 24, paddingBottom: 48 }}>
      <div className="reward-toolbar">
        <div>
          <h1 style={{ margin: 0 }}>🎁 點數兌換</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)' }}>用累積的點數兌換好物，提出申請後由審核者確認。</p>
        </div>
        <div className="reward-balance">剩餘點數 <strong>{remaining}</strong></div>
      </div>

      <div className="reward-subtabs">
        <button className={`btn btn-sm ${tab === 'shop' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('shop')}>
          商城{items.length > 0 && <span className="reward-tab-count">{items.length}</span>}
        </button>
        <button className={`btn btn-sm ${tab === 'mine' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('mine')}>
          我的兌換{myRedemptions.length > 0 && <span className="reward-tab-count">{myRedemptions.length}</span>}
        </button>
        {flags.canReview && (
          <button className={`btn btn-sm ${tab === 'review' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('review')}>
            兌換審核{reviewCount > 0 && <span className="reward-tab-count is-alert">{reviewCount}</span>}
          </button>
        )}
        {flags.canCreate && tab === 'shop' && <button className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setEditingItem('new')}>＋ 上架商品</button>}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {tab === 'shop' && (
        loading ? (
          <div className="reward-empty">載入中...</div>
        ) : items.length === 0 ? (
          <div className="reward-empty">目前沒有可兌換的商品</div>
        ) : (
          <div className="reward-grid">
            {items.map((it) => {
              const soldOut = it.stock !== null && it.stock !== undefined && it.stock <= 0;
              return (
                <article className={`reward-card ${it.status !== 'ACTIVE' ? 'disabled' : ''}`} key={it.id}>
                  {it.image_url
                    ? <img className="reward-card-image" src={it.image_url} alt={it.name} />
                    : <div className="reward-card-image placeholder">🎁</div>}
                  <div className="reward-card-body">
                    <h3 className="reward-card-title">{it.name}</h3>
                    {it.description && <p className="reward-card-desc">{it.description}</p>}
                    <div className="reward-card-meta">
                      <span className="reward-points">{it.points_cost}<small>點</small></span>
                      <span className={`reward-stock ${soldOut ? 'soldout' : ''}`}>
                        {it.stock === null || it.stock === undefined ? '無限量' : (soldOut ? '已兌完' : `庫存 ${it.stock}`)}
                      </span>
                    </div>
                    <div className="reward-card-actions">
                      <button className="btn btn-sm btn-primary" disabled={soldOut} onClick={() => setRedeemItem(it)}>兌換</button>
                      {flags.canCreate && <button className="btn btn-sm btn-secondary" onClick={() => setEditingItem(it)}>編輯</button>}
                      {flags.canCreate && <button className="btn btn-sm btn-danger" onClick={() => handleDelete(it.id)}>下架</button>}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )
      )}

      {tab === 'mine' && (
        myRedemptions.length === 0 ? (
          <div className="reward-empty">你還沒有任何兌換紀錄</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="reward-redemption-table">
              <thead>
                <tr><th>申請時間</th><th>商品</th><th>數量</th><th>點數</th><th>狀態</th><th>備註</th><th>操作</th></tr>
              </thead>
              <tbody>
                {myRedemptions.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDate(r.created_at)}</td>
                    <td>{r.reward_name}</td>
                    <td>{r.quantity}</td>
                    <td>{r.total_points}</td>
                    <td><span className={`reward-status-badge reward-status-${r.status}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                    <td title={r.review_note ? `審核備註：${r.review_note}` : ''}>{r.review_note || r.note || '-'}</td>
                    <td>{r.status === 'PENDING' ? <button className="btn btn-sm btn-secondary" onClick={() => handleCancel(r.id)}>取消</button> : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === 'review' && flags.canReview && <RewardReviewList onReviewed={() => { loadBalance(); loadReviewCount(); }} />}

      {redeemItem && (
        <RewardRedeemModal item={redeemItem} remaining={remaining} onClose={() => setRedeemItem(null)} onSubmitted={handleRedeemed} />
      )}
      {editingItem && (
        <RewardItemModal item={editingItem === 'new' ? null : editingItem} onClose={() => setEditingItem(null)} onSaved={loadItems} />
      )}
    </div>
  );
};

export default RewardShopPage;
